// Shown inside the Drawer on the right.
// Shows image metadata (log name, timestamp, etc.), relevant links, and also the 'Sent to Annotations' status if applicable.

import { FC, useMemo } from 'react';
import { useJobStatusAPI } from 'packages/lib/api/logminer/fetch-hooks';
import { StateIcon } from 'packages/lib/common-ui/state-icon';
import styled from '@emotion/styled';
import {
  KeyValueTable,
  KeyValueTableRow,
} from 'packages/lib/common-ui/keyValueTable';
import { OpenInNewIcon } from 'packages/lib/common-ui/icons/open-in-new';
import { Typography, Skeleton } from 'antd';
import { OmnitagResultItem } from 'packages/lib/api/omnitag/contract/omnitag-search';
import { useRecoilValue } from 'recoil';
import { uiThemeAtom } from 'packages/at-shell/state/recoil/ui';
import { useTenant } from 'packages/lib/auth/hooks/tenant';

// Pylon URL param reference:
// https://confluence.ci.motional.com/confluence/display/BACK/Pylon+URL+Parameter+Documentation
export function getPylonLink(obj: {
  logName: string;
  timestamp: string;
}): string {
  return `https://drivelogs.data.motional.com/pylon/index.html?logslice=${
    obj.logName
  }&timestamp=${Number(obj.timestamp) / 1e6}`;
}

export function getSceneHuntingLink(obj: {
  logName: string;
  timestamp: string;
  cameraChannel: string | undefined;
}): string {
  return `/scene-hunting?log=${obj.logName}&curTimeAbs=${obj.timestamp}&channel=${obj.cameraChannel}`;
}

const MetaData = styled.div`
  .label {
    margin-right: 1rem;
    margin-bottom: 0.5rem;
    display: inline-block;
  }

  div:not(:root):fullscreen & {
    display: block;
  }
`;

const styles: Record<string, React.CSSProperties> = {
  flexBox: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: '2em',
    marginBottom: '1em',
    marginLeft: '1em',
    marginRight: '1em',
  },
};

interface OTMetadataPanelProps {
  metadata: OmnitagResultItem;
  onClose: () => void;
}

export const OTMetadataPanel: FC<
  React.PropsWithChildren<OTMetadataPanelProps>
> = ({ metadata }) => {
  const theme = useRecoilValue(uiThemeAtom);
  const timestampStr = metadata.timestamp.toString();
  const { jobStatus, isLoading } = useJobStatusAPI(
    metadata.log_name,
    timestampStr
  );
  const { data: tenant } = useTenant();
  const isContractorToken = tenant === 'contractor';

  const linkBlueColor = theme === 'dark' ? '#6babfeff' : 'default';

  const sendToAnnotationStatusBox: JSX.Element | null =
    jobStatus && jobStatus.status ? (
      <div style={{ marginTop: '2em' }}>
        <Typography.Title level={5}>Sent to Annotations</Typography.Title>
        <div style={{ fontSize: '12px', marginLeft: '1em' }}>
          <b>Status:</b>
          <StateIcon
            state={jobStatus.status}
            style={{
              marginLeft: '6px',
            }}
          />
          {jobStatus.status}
          <br />
          <b>Sent by:</b>&nbsp;{jobStatus.user_name}
          <br />
          <b>Date:</b>&nbsp;
          {new Date(jobStatus.updated_timestamp).toLocaleString()}
        </div>
      </div>
    ) : isLoading ? (
      <>
        <div style={{ marginTop: '2em' }}>
          <Typography.Title level={5}>Sent to Annotations</Typography.Title>
          <Skeleton active title={false} paragraph={{ rows: 3 }} />
        </div>
      </>
    ) : null;

  const pylonLink = getPylonLink({
    logName: metadata.log_name,
    timestamp: timestampStr,
  });

  const sceneHuntingLink = getSceneHuntingLink({
    logName: metadata.log_name,
    timestamp: timestampStr,
    cameraChannel: metadata.channel,
  });

  const metadataTable1: KeyValueTableRow[] = useMemo(
    () => [
      {
        key: 'Log Name',
        value: metadata.log_name,
        maxStrLen: 19,
        allowCopy: true,
      },
      { key: 'Log Date', value: metadata.log_date },
      { key: 'Camera Channel', value: metadata.channel },
      { key: 'Timestamp', value: timestampStr, type: 'formattedTimesstamp' },
      ...(metadata.extraction_token
        ? [
            {
              key: 'Extraction Token',
              value: metadata.extraction_token,
              allowCopy: true,
            },
          ]
        : []),
    ],
    [metadata, timestampStr]
  );

  const metadataTable2: KeyValueTableRow[] = useMemo(() => {
    const data = [
      { key: 'Distance to Ego', value: metadata.distance_to_ego },
      { key: 'Track ID', value: metadata.track_id },
      { key: 'Track Type', value: metadata.track_type },
      { key: 'IOU', value: metadata.iou },
      { key: 'Tag Probability', value: metadata.tag_probability },
      { key: 'Route Name', value: metadata.route_name },
      { key: 'City', value: metadata.city },
      { key: 'Test Type', value: metadata.test_type },
      { key: 'Test Sub Type', value: metadata.test_sub_type },
      { key: 'Software Version', value: metadata.software_version },
      { key: 'Sun Lighting Condition', value: metadata.sun_lighting_condition },
      { key: 'Weather Condition', value: metadata.weather_condition },
      { key: 'Surface Type', value: metadata.surface_type },
    ];
    if (metadata.ego_location) {
      data.push({ key: 'Ego Location', value: metadata.ego_location });
    }
    return data;
  }, [metadata]);

  const railLink = metadata.extraction_token
    ? `https://ml.motional.com/lidar-box/${metadata.extraction_token}?cameraChannel=${metadata.channel}&closestTimestamp=${metadata.timestamp}`
    : '';

  return (
    <>
      <div style={styles.flexBox}>
        {!isContractorToken && (
          <div>
            <a
              href={pylonLink}
              target="_blank"
              rel="noreferrer"
              style={{ color: linkBlueColor }}
            >
              <div>
                Open in Pylon
                <OpenInNewIcon />
              </div>
            </a>
          </div>
        )}
        <div>
          <a
            href={sceneHuntingLink}
            target="_blank"
            rel="noreferrer"
            style={{ color: linkBlueColor }}
          >
            <div>
              Open in Scene Hunting <OpenInNewIcon />
            </div>
          </a>
        </div>
        {metadata.extraction_token && (
          <div>
            <a
              href={railLink}
              target="_blank"
              rel="noreferrer"
              style={{ color: linkBlueColor }}
            >
              <div>
                Open in RAIL
                <OpenInNewIcon />
              </div>
            </a>
          </div>
        )}
      </div>
      <MetaData>
        <KeyValueTable
          tableNameKey="metadata-table-1"
          data={metadataTable1}
          keyColWidth={160}
          maxValueWidth={80}
          rowHeight={5}
        />
        <Typography.Title level={5} style={{ marginTop: '2em' }}>
          Metadata
        </Typography.Title>
        <KeyValueTable
          tableNameKey="metadata-table-2"
          data={metadataTable2}
          keyColWidth={160}
          maxValueWidth={80}
          rowHeight={5}
        />
      </MetaData>
      {sendToAnnotationStatusBox}
    </>
  );
};
